import {gql} from '@apollo/client';
import React, {useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import InventoryStructuredTableMode, {
  InventoryTableInsertionTableType,
} from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {
  TransferStructuredTableBarcodingStrategy,
  TransferStructuredTableDestinationType,
  TransferStructuredTableMetadata,
  TransferStructuredTableSourceType,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import InventoryTableInsertionModeToggle, {
  InventoryTableInsertionMode,
} from 'notebook/structured-tables/components/InventoryTableInsertionModeToggle';
import useIncludeSampleControlRoles from 'notebook/structured-tables/components/useIncludeSampleControlRoles';
import useSelectFolderOptionForTable from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {
  ContainerSchema,
  PlateSchema,
  PlateSchemaType,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import usePrevious from 'ui-library/hooks/usePrevious';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

import {GetContainerSchemasQuery} from './__generated/GetContainerSchemasQuery';

export interface TransferTableInsertModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (
    insertionConfig: Exclude<TransferStructuredTableInsertionConfiguration, 'INVALID'>,
    selectorsById: {[selectorId: string]: SchemaFieldSelector}
  ) => void;
  structuredTableInsertionContext?: StructuredTableInsertionContextValue;
}

/**
 * The transfer table insert modal can insert two different kinds of tables:
 * - The legacy transfer table, which is specified by a container schema configured using
 *   LegacyTransferTableInsertForm.
 * - The transfer structured table, which is specified by a TransferStructuredTableInsertionConfiguration
 *   configured using a TransferStructuredTableInsertForm.
 */
export default function TransferTableInsertModal({
  onRequestClose,
  onRequestSubmit,
}: TransferTableInsertModalProps): JSX.Element {
  const [mode, setMode] = useState<InventoryTableInsertionMode>(InventoryStructuredTableMode.CREATE);
  const [submitted, setSubmitted] = useState(false);
  const [structuredTableInsertionConfig, setStructuredTableInsertionConfig] =
    useState<TransferStructuredTableInsertionConfiguration>('INVALID');
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});

  return (
    <Modal
      title="Insert transfer table"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (submitted) {
          // prevent double clicks
          return;
        }
        setSubmitted(true);
        assert(
          structuredTableInsertionConfig !== 'INVALID' && selectorsAreLoaded(selectorsById),
          'Expecting structuredTableInsertionConfig to be valid and selectorsById to be loaded when submit button is enabled'
        );
        onRequestSubmit(structuredTableInsertionConfig, selectorsById);
      }}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled:
            submitted ||
            structuredTableInsertionConfig === 'INVALID' ||
            selectorsById === AsyncStatus.LOADING ||
            selectorsById === AsyncStatus.ERROR,
          dataTestAttrs: {
            component: 'TransferTableInsertModal',
            element: 'button',
            key: ModalButtonKey.INSERT,
          },
        },
      ]}
      dataTestAttrs={{component: 'TransferTableInsertModal', element: 'root'}}
      className="mediocre" // TableEditable's CSS is namespaced under mediocre
    >
      <InventoryTableInsertionModeToggle
        currentMode={mode}
        setMode={(newMode: InventoryTableInsertionMode) => {
          setMode(newMode);
        }}
        includeInsertionModes={[InventoryStructuredTableMode.CREATE, InventoryStructuredTableMode.FILL]}
      />
      <TransferStructuredTableInsertForm
        mode={mode as InventoryStructuredTableMode}
        onChange={setStructuredTableInsertionConfig}
      />
    </Modal>
  );
}

export type TransferStructuredTableInsertionConfiguration =
  | {
      tableType: InventoryTableInsertionTableType.TRANSFER;
      metadata: TransferStructuredTableMetadata;
      destinationPlateSchema: PlateSchema | null;
      destinationContainerSchema: ContainerSchema;
    }
  | 'INVALID';

export const GET_CONTAINER_SCHEMAS_QUERY = gql`
  query GetContainerSchemasQuery @skipAudit {
    containerSchemas {
      ...ContainerSchemaGraphQL
    }
  }
  ${getFragmentWrapper('ContainerSchema').fragment}
`;

export function TransferStructuredTableInsertForm({
  mode,
  onChange,
  structuredTableInsertionContext,
}: {
  mode: InventoryStructuredTableMode;
  onChange: (newInsertionConfig: TransferStructuredTableInsertionConfiguration) => void;
  structuredTableInsertionContext?: StructuredTableInsertionContextValue;
}): JSX.Element {
  const [sourceType, setSourceType] = useState(TransferStructuredTableSourceType.BIOENTITY);
  const {destinationType, isDestinationTypeValid, destinationTypeSelector} =
    stubbableFunctions.useDestinationType(mode);
  const {destinationSchema, isDestinationSchemaValid, destinationSchemaSelector} =
    stubbableFunctions.useDestinationSchema(destinationType);
  const {barcodingStrategy, isBarcodingStrategyValid, barcodingStrategyToggle} = useBarcodingStrategy(mode);
  const {includeSampleRoles, isIncludeSampleRolesValid, includeSampleRolesCheckbox} =
    useIncludeSampleControlRoles({
      canHaveSampleRoles: destinationType !== TransferStructuredTableDestinationType.PLATE,
    });

  const {selectedFolderConfig, isInvalidFolderConfig, folderOptionSelector} = useSelectFolderOptionForTable({
    itemTypeSupportsFolders: false,
    isForCreate: mode === InventoryStructuredTableMode.CREATE,
    projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES,
    useLegacyLabel: true,
    structuredTableInsertionContext,
  });
  const isFormStateValid =
    isDestinationTypeValid &&
    isDestinationSchemaValid &&
    isBarcodingStrategyValid &&
    isIncludeSampleRolesValid;

  // Query for all container schemas, including the plate well schema
  // Skip if user hasn't selected destinationSchema yet, or they selected a container schema already.
  const containerSchemaQueryResult = useSkippableBenchlingQuery<GetContainerSchemasQuery>(
    GET_CONTAINER_SCHEMAS_QUERY,
    {
      skip: APIUtil.isContainerSchema(destinationSchema?.id ?? '') || !destinationSchema,
    }
  );

  // destinationContainerSchema can be any container schema (including plate wells)
  let destinationContainerSchema: ContainerSchema | null = null;
  let destinationPlateSchema: PlateSchema | null = null;

  if (destinationSchema) {
    if (APIUtil.isContainerSchema(destinationSchema.id)) {
      destinationContainerSchema = destinationSchema;
    } else if (
      containerSchemaQueryResult.type === QueryResultType.SUCCESS &&
      APIUtil.isPlateSchema(destinationSchema.id)
    ) {
      destinationPlateSchema = destinationSchema as PlateSchema;
      const containerSchemaGQL = containerSchemaQueryResult.data.containerSchemas.find(
        // We know destinationSchema is not null, so destinationPlateWellSchema is not null as well.
        (containerSchema) => containerSchema.id === destinationPlateSchema!.containerSchemaId
      );
      assert(containerSchemaGQL != null, "Could not find the container schema for the plate's wells");

      destinationContainerSchema = graphQLToREST('ContainerSchema', containerSchemaGQL);
    }
  }

  // This will trigger the useEffect on every render, but that's okay, because we do a deep-equality check.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dependenciesForOnChange = {
    mode,
    sourceType,
    destinationType,
    destinationContainerSchema,
    destinationPlateSchema,
    barcodingStrategy,
    isFormStateValid,
    containerSchemaQueryResultType: containerSchemaQueryResult.type,
    includeSampleRoles,
    selectedFolderConfig,
    isInvalidFolderConfig,
  };
  const previousDependenciesForOnChange = usePrevious(dependenciesForOnChange);
  useEffect(() => {
    // If destinationContainerSchema or destinationPlateSchema are copied,
    // this useEffect will get triggered. But if everything is still deep-equal
    // to their previous values, we shouldn't call onChange, or we might get into an infinite loop.
    //
    // Why do we need to do a deep equality check? After all, it's officially discouraged
    // (see https://github.com/facebook/react/issues/14476#issuecomment-471199055).
    // By doing a deep equality check here, we avoid the need to memoize the schema objects,
    // destinationContainerSchema and destinationPlateSchema. The schema objects are tricky to memoize,
    // because we would need to dig into the internals of our GraphQL library.
    //
    // With deep equality checks, the main risk is performance. But performance should be okay here,
    // because we're not comparing a ton of data, just two schema objects.
    if (isEqual(dependenciesForOnChange, previousDependenciesForOnChange)) {
      return;
    }

    const {
      mode,
      sourceType,
      destinationType,
      destinationContainerSchema,
      destinationPlateSchema,
      barcodingStrategy,
      isFormStateValid,
      containerSchemaQueryResultType,
      includeSampleRoles,
      selectedFolderConfig,
      isInvalidFolderConfig,
    } = dependenciesForOnChange;

    const parentStorableIsGrid = [
      TransferStructuredTableDestinationType.BOX,
      TransferStructuredTableDestinationType.MATRIX_PLATE,
      TransferStructuredTableDestinationType.PLATE,
    ].includes(destinationType);

    if (
      !isInvalidFolderConfig &&
      isFormStateValid &&
      containerSchemaQueryResultType !== QueryResultType.PENDING
    ) {
      onChange({
        // Keep validation logic in sync with the TransferStructuredTableMetadata type definition.
        tableType: InventoryTableInsertionTableType.TRANSFER,
        metadata: {
          mode,
          sourceType,
          destinationType,
          barcodingStrategy,
          parentStorableIsGrid,
          canChangeUnits: true,
          searchAllSchemas: true,
          includeSampleRoles,
          ...selectedFolderConfig,
        } as TransferStructuredTableMetadata,
        destinationContainerSchema: destinationContainerSchema!,
        destinationPlateSchema,
      });
    } else {
      onChange('INVALID');
    }
  }, [dependenciesForOnChange, previousDependenciesForOnChange, onChange]);

  return (
    <>
      <FormGroup dataTestAttrs={{component: 'TransferTableInsertModal', element: 'form', key: 'source'}}>
        <Label>Source</Label>
        <EnumSelectDropdown
          options={SOURCE_TYPE_OPTIONS}
          value={sourceType}
          onChange={(newSourceType) => {
            setSourceType(newSourceType);
          }}
        />
      </FormGroup>
      {destinationTypeSelector}
      {destinationSchemaSelector}
      {barcodingStrategyToggle}
      {folderOptionSelector}
      {includeSampleRolesCheckbox}
    </>
  );
}

export const stubbableFunctions = {
  useDestinationSchema(destinationType: TransferStructuredTableDestinationType): {
    destinationSchema: StorableSchema | null;
    isDestinationSchemaValid: boolean;
    destinationSchemaSelector: JSX.Element;
  } {
    const [destinationSchema, setDestinationSchema] = useState<StorableSchema | null>(null);

    // If the user switches the destination type to plate while a container schema is selected, or vice versa,
    // the destination schema is now invalid, so reset it.
    const isDestinationSchemaValid =
      destinationSchema != null &&
      (destinationType === TransferStructuredTableDestinationType.PLATE) ===
        APIUtil.isPlateSchema(destinationSchema.id ?? '');
    useEffect(() => {
      if (!isDestinationSchemaValid) {
        setDestinationSchema(null);
      }
    }, [isDestinationSchemaValid]);

    const destinationSchemaSelector = (
      <FormGroup
        dataTestAttrs={{
          component: 'TransferTableInsertModal',
          element: 'form',
          key: 'destinationSchemaSelector',
        }}
      >
        {destinationType === TransferStructuredTableDestinationType.PLATE ? (
          <>
            <Label>Destination plate schema</Label>
            <PlateSchemaSelector
              onChange={setDestinationSchema}
              plateSchemaType={PlateSchemaType.FIXED_PLATE}
              // Avoids passing a container schema ID to the plate schema selector
              selectedPlateSchemaId={isDestinationSchemaValid ? destinationSchema.id : null}
            />
          </>
        ) : (
          <>
            <Label>Destination container schema</Label>
            <ContainerSchemaSelector
              onChange={setDestinationSchema}
              // Avoids passing a plate schema ID to the container schema selector
              selectedContainerSchemaId={isDestinationSchemaValid ? destinationSchema.id : null}
              shouldShowNoSchemaOption={false}
            />
          </>
        )}
      </FormGroup>
    );

    return {destinationSchema, isDestinationSchemaValid, destinationSchemaSelector};
  },

  useDestinationType(mode: InventoryStructuredTableMode): {
    destinationType: TransferStructuredTableDestinationType;
    isDestinationTypeValid: boolean;
    destinationTypeSelector: JSX.Element;
  } {
    const [destinationType, setDestinationType] = useState(TransferStructuredTableDestinationType.CONTAINER);

    // This table can be used to fill plates, but not create them.
    const availableDestinationTypeOptions = useMemo(
      () =>
        mode === InventoryStructuredTableMode.CREATE
          ? OPTIONS_FOR_DESTINATION_CONTAINER_LOCATION
          : OPTIONS_FOR_DESTINATION_TO_FILL,
      [mode]
    );

    // If the user switches to create mode with a plate destination selected, reset the destination type to
    // a valid option.
    const isDestinationTypeValid = availableDestinationTypeOptions
      .map((option) => option.key)
      .includes(destinationType);
    useEffect(() => {
      if (!isDestinationTypeValid) {
        setDestinationType(availableDestinationTypeOptions[0].key);
      }
    }, [availableDestinationTypeOptions, isDestinationTypeValid]);

    const destinationTypeSelector = (
      <FormGroup
        dataTestAttrs={{component: 'TransferTableInsertModal', element: 'form', key: 'containerDestination'}}
      >
        <Label>
          {mode === InventoryStructuredTableMode.CREATE ? 'Container destination' : 'Destination to fill'}
        </Label>
        <EnumSelectDropdown
          options={availableDestinationTypeOptions}
          value={destinationType}
          onChange={(newDestinationType) => {
            setDestinationType(newDestinationType);
          }}
        />
      </FormGroup>
    );

    return {destinationType, isDestinationTypeValid, destinationTypeSelector};
  },
};

function useBarcodingStrategy(mode: InventoryStructuredTableMode): {
  barcodingStrategy: TransferStructuredTableBarcodingStrategy | null;
  isBarcodingStrategyValid: boolean;
  barcodingStrategyToggle: JSX.Element | null;
} {
  const [barcodingStrategy, setBarcodingStrategy] = useState<TransferStructuredTableBarcodingStrategy | null>(
    TransferStructuredTableBarcodingStrategy.AUTOGENERATE
  );

  // Ensure a barcoding strategy is selected iff in create mode.
  const isBarcodingStrategyValid =
    (barcodingStrategy != null) === (mode === InventoryStructuredTableMode.CREATE);
  useEffect(() => {
    if (!isBarcodingStrategyValid) {
      setBarcodingStrategy(
        mode === InventoryStructuredTableMode.CREATE
          ? TransferStructuredTableBarcodingStrategy.AUTOGENERATE
          : null
      );
    }
  }, [mode, isBarcodingStrategyValid]);

  const barcodingStrategyToggle =
    mode === InventoryStructuredTableMode.CREATE ? (
      <FormGroup>
        <RadioButtonGroup
          title="Barcodes"
          options={[
            {value: TransferStructuredTableBarcodingStrategy.AUTOGENERATE, label: 'Autogenerate'},
            {value: TransferStructuredTableBarcodingStrategy.CUSTOM, label: 'Custom'},
          ]}
          onChange={(newBarcodingStrategy: TransferStructuredTableBarcodingStrategy) => {
            setBarcodingStrategy(newBarcodingStrategy);
          }}
          selectedValue={barcodingStrategy}
          isVertical={true}
        />
      </FormGroup>
    ) : null;

  return {barcodingStrategy, isBarcodingStrategyValid, barcodingStrategyToggle};
}

const SOURCE_TYPE_OPTIONS = [
  {key: TransferStructuredTableSourceType.BIOENTITY, label: 'Entity'},
  {key: TransferStructuredTableSourceType.CONTAINER, label: 'Container'},
  {key: TransferStructuredTableSourceType.PLATE, label: 'Plate'},
];

const OPTIONS_FOR_DESTINATION_CONTAINER_LOCATION = [
  {key: TransferStructuredTableDestinationType.BOX, label: 'Box'},
  {key: TransferStructuredTableDestinationType.CONTAINER, label: 'Location'},
  {key: TransferStructuredTableDestinationType.MATRIX_PLATE, label: 'Matrix Plate'},
];
const OPTIONS_FOR_DESTINATION_TO_FILL = [
  {key: TransferStructuredTableDestinationType.PLATE, label: 'Plate'},
  {key: TransferStructuredTableDestinationType.CONTAINER, label: 'Container'},
];

/**
 * A simple select dropdown for use with a string enum.
 */
function EnumSelectDropdown<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{key: T; label: string}>;
  value: T; // If value is missing from options, the current label will be rendered as blank.
  onChange: (newValue: T) => void;
}): JSX.Element {
  const [displayName] = options.filter((option) => option.key === value).map((option) => option.label) as [
    string | undefined,
  ];

  return (
    <SelectDropdown
      displayName={displayName ?? ''}
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
          {options.map(({key, label}) => (
            <Option
              key={key}
              value={label}
              onSelect={(selectedLabel: T) => {
                const [selectedKey] = options
                  .filter((option) => option.label === selectedLabel)
                  .map((option) => option.key);
                onChange(selectedKey);
              }}
            >
              {label}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}
